import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  shopApi, 
  xoxoShopApi, 
  Shop, 
  kioskTypesSeedApi, 
  kioskInstanceApi 
} from '@xoxokiosk/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Alert, AlertDescription } from '@xoxokiosk/ui';
import { AlertCircle, RefreshCw, Trash2, Store } from 'lucide-react';
  

const ShopAdminPage: React.FC = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  // Removed selectedShop and recipes state; will move to ShopDashboardPage
  const navigate = useNavigate();


  const [seedStatus, setSeedStatus] = useState('');
  const [nukeStatus, setNukeStatus] = useState('');
  const [kioskTypesSeedStatus, setKioskTypesSeedStatus] = useState('');
  // Nuke all shops handler
  const [nukeShopsStatus, setNukeShopsStatus] = useState('');
  const handleNukeAllShops = async () => {
    if (!window.confirm('Are you sure you want to permanently delete ALL shops? This action cannot be undone.')) {
      return;
    }
    setNukeShopsStatus('Nuking all shops...');
    try {
      await shopApi.nukeAllShops();
      setNukeShopsStatus('All shops deleted.');
      await fetchShops();
    } catch (error) {
      setNukeShopsStatus('Failed to delete all shops.');
      console.error(error);
    }
  };
  const handleReseedKioskTypes = async () => {
    setKioskTypesSeedStatus('Reseeding kiosk types...');
    try {
      const response = await kioskTypesSeedApi.reseedKioskTypes();
      setKioskTypesSeedStatus(response.data.message || 'Kiosk types reseeded!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setKioskTypesSeedStatus(`Failed to reseed kiosk types: ${errorMessage}`);
      console.error('Error in handleReseedKioskTypes:', error);
    }
  };
  const handleNukeAllInstances = async () => {
    if (!window.confirm('Are you sure you want to permanently delete ALL kiosk instances for ALL shops? This action cannot be undone.')) {
      return;
    }
    setNukeStatus('Nuking all kiosk instances...');
    try {
      const response = await kioskInstanceApi.bulkDeleteAllInstances();
      if (response.data.success) {
        setNukeStatus(`Successfully deleted ${response.data.deletedInstances} kiosk instance(s).`);
      } else {
        setNukeStatus(response.data.message || 'Failed to delete all kiosk instances.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setNukeStatus(`Failed to delete kiosk instances: ${errorMessage}`);
      console.error('Error in handleNukeAllInstances:', error);
    }
    await fetchShops();
  };

  const decodeJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error decoding JWT:', e);
      return null;
    }
  };

  const fetchShops = async () => {
    try {
      console.log('Fetching shops from XOXO API...');
      const response = await xoxoShopApi.getAllShops();
      console.log('Shops fetched successfully from XOXO API:', response.data);
      setShops(response.data);
    } catch (error: any) {
      console.error('Failed to fetch shops:', error);
      
      if (error.response) {
        if (error.response.status === 401) {
          // Handle unauthorized - likely need to log in again
          console.error('Authentication required - redirecting to login');
          navigate('/login');
          return;
        }
        
        console.error('API Error:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('No response received from server');
      } else {
        console.error('Request setup error:', error.message);
      }
      
      // Show error to user
      alert('Failed to load shops. Please try again later.');
    }
  };

  const handleUpsertShops = async () => {
    setSeedStatus('Syncing shops from XOXO API...');
    try {
      const response = await shopApi.upsertShops();
      setSeedStatus('Shops synced successfully!');
      await fetchShops();
    } catch (error) {
      console.error('Error upserting shops:', error);
      setSeedStatus('Failed to sync shops.');
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Shop Administration</h1>
            <p className="text-muted-foreground mt-2">Manage shops and kiosk configurations</p>
          </div>
        </div>

        {/* Admin Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Admin Actions</CardTitle>
            <CardDescription>Sync data and manage system-wide configurations</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleUpsertShops} variant="default">
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Shops from XOXO API
              </Button>
              <Button onClick={handleReseedKioskTypes} variant="secondary">
                <RefreshCw className="mr-2 h-4 w-4" />
                Reseed Kiosk Types
              </Button>
              <Button onClick={handleNukeAllInstances} variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Nuke All Kiosk Instances
              </Button>
              <Button onClick={handleNukeAllShops} variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Nuke All Shops
              </Button>
            </div>

            {/* Status Messages */}
            <div className="space-y-2">
              {seedStatus && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{seedStatus}</AlertDescription>
                </Alert>
              )}
              {kioskTypesSeedStatus && (
                <Alert variant={kioskTypesSeedStatus.startsWith('Failed') ? 'destructive' : 'default'}>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{kioskTypesSeedStatus}</AlertDescription>
                </Alert>
              )}
              {nukeStatus && (
                <Alert variant={nukeStatus.startsWith('Failed') ? 'destructive' : 'default'}>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{nukeStatus}</AlertDescription>
                </Alert>
              )}
              {nukeShopsStatus && (
                <Alert variant={nukeShopsStatus.startsWith('Failed') ? 'destructive' : 'default'}>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{nukeShopsStatus}</AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Shops Grid */}
        <Card>
          <CardHeader>
            <CardTitle>Shops</CardTitle>
            <CardDescription>Select a shop to manage its kiosks and settings</CardDescription>
          </CardHeader>
          <CardContent>
            {shops.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shops.map((shop) => (
                  <Card 
                    key={shop.id}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => navigate(`/shops/${shop.id}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-primary/10 rounded-lg">
                          <Store className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-foreground">{shop.name}</h3>
                          <p className="text-sm text-muted-foreground">ID: {shop.id}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No shops available. Sync shops from XOXO API to get started.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ShopAdminPage;
